import { useEffect, useMemo } from 'react';
import { useInfiniteQuery } from 'react-query';

import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { instructionsApi } from 'src/Services/API';

const INSTRUCTIONS_PER_PAGE = 10;

const useFetchInstructions = () => {
  const assetId = useCurrentAssetId();

  const query = useInfiniteQuery({
    queryKey: ['fetch-instructions', assetId],
    queryFn: async ({ pageParam = 1 }) => {
      if (!assetId) throw new Error('No assetId provided');

      return (
        await instructionsApi.instructionsControllerFindAll(
          assetId,
          pageParam,
          INSTRUCTIONS_PER_PAGE
        )
      ).data;
    },
    getNextPageParam: lastPage => {
      if (!lastPage) return undefined;
      return lastPage.currentPage < lastPage.pageCount ? lastPage.currentPage + 1 : undefined;
    },
    enabled: Boolean(assetId),
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });

  useEffect(() => {
    const loadAllPages = async () => {
      if (query.hasNextPage && !query.isFetchingNextPage) {
        await query.fetchNextPage();
      }
    };

    loadAllPages();
  }, [query.hasNextPage, query.isFetchingNextPage, query.fetchNextPage]);

  const allInstructions = useMemo(
    () => query.data?.pages.flatMap(page => page?.items ?? []) ?? [],
    [query.data]
  );

  return {
    data: allInstructions,
    isLoading: query.isLoading || query.isFetchingNextPage,
    error: query.error,
  };
};
export default useFetchInstructions;
